import {
  REFERRAL_MSG,
  isReferralTarget,
  marginOfPrice,
  markupOfPrice,
  pct2,
  repriceLine,
  saleOf,
  type LineInput,
  type LinePricingEdit,
  type ReferralLineFee,
} from "@genbaryoku/contract";
import { useEffect, useRef, useState } from "react";
import { t } from "../labels.js";
import { yen } from "../lib/num.js";
import { NumInput } from "./NumInput.jsx";
import { Help } from "./ui.jsx";

/* ---------------------------------------------------------------------------
 * パートナー手数料（#213、docs/40 §5・§6）の画面の部品。計算は contract の referral.ts（ここは見せ方と欄だけ）。
 *   PctField    ％の欄（小数2桁。全角も直す。打ち終わり＝欄を出る・Enter で渡す）
 *   FeeMark     仕入元の欄の下段の手数料「P 14,286」（見積の率と違う行は「P 2% 2,564」「P 0%」）。藍の小さい字（★D）
 *   LinePricing 行の詳細の下段：単価［粗利率｜上乗せ］・率の欄・＋パートナー・＝上乗せ率（docs/40 §5.2 の「どれを直すと何が変わるか」。#215 で「上乗せ」→「上乗せ率」。
 *               #314（11/5 射場さん ★6）：「率で｜円で」→「粗利率｜上乗せ」。切替が欄の名前＝欄の頭に「粗利率」「上乗せ」を重ねて出さない（読み上げの名前は残す））
 * ------------------------------------------------------------------------- */

/** ％の見せ方（2 → "2"・2.5 → "2.5"・10.25 → "10.25"） */
export const pctShort = (v: number): string => String(Math.round(v * 100) / 100);
/** 欄の中の ％（小数2桁で揃える：20 → "20.00"） */
const pctFixed = (v: number | null): string => (v == null || !Number.isFinite(v) ? "" : v.toFixed(2));

/**
 * ％の欄（小数2桁）。空は null。値が変わらなければ渡さない。渡すのは打ったときだけ（Enter・欄を出る）。
 * 打っていない間は親の値をそのまま見せる（#215：仕入れの Enter で粗利率の欄に移った直後に単価が入ると、粗利率もその場で見える。中身は選んだまま＝打てば置き換わる）
 */
export function PctField({ value, onCommit, ...rest }: Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> & { value: number | null; onCommit: (v: number | null) => void }) {
  const [text, setText] = useState(() => pctFixed(value));
  const focused = useRef(false);
  /** 欄に入ってから（か前に渡してから）打ったか。Enter で渡した直後の blur で2回渡さない */
  const typed = useRef(false);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (typed.current) return; // 打ちかけは消さない
    setText(pctFixed(value));
    if (focused.current) requestAnimationFrame(() => document.activeElement === ref.current && ref.current?.select());
  }, [value]);
  const commit = () => {
    if (!typed.current) return;
    typed.current = false;
    const h = text.replace(/[０-９．－]/g, (c) => (c === "－" ? "-" : String.fromCharCode(c.charCodeAt(0) - 0xfee0))).replace(/[^\d.-]/g, "");
    const v = h === "" ? null : Number(h);
    const next = v == null || Number.isNaN(v) ? null : Math.round(v * 100) / 100;
    setText(pctFixed(next ?? value));
    if (next !== value) onCommit(next);
  };
  return (
    <input
      {...rest}
      ref={ref}
      className={"ce r num pct " + (rest.className ?? "")}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={text}
      onChange={(e) => {
        typed.current = true;
        setText(e.target.value);
      }}
      onFocus={(e) => {
        focused.current = true;
        e.currentTarget.select();
      }}
      onBlur={() => {
        focused.current = false;
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.nativeEvent.isComposing) {
          e.preventDefault();
          commit();
        }
      }}
    />
  );
}

/** 仕入元の欄の下段：手数料（見積の率と違う行は率も）。表の行の高さは 44px のまま（上 28px・下 14px） */
export function FeeMark({ f }: { f: ReferralLineFee }) {
  const text = f.custom ? (f.pct === 0 ? `${REFERRAL_MSG.feeMark} 0%` : `${REFERRAL_MSG.feeMark} ${pctShort(f.pct)}% ${yen(f.fee)}`) : `${REFERRAL_MSG.feeMark} ${yen(f.fee)}`;
  return (
    <span className={"srcfee" + (f.custom ? " is-custom" : "")} data-testid="fee-mark" title={t(`{referrer} ${pctShort(f.pct)}%：手数料 ${yen(f.fee)}`)}>
      {text}
    </span>
  );
}

export type PricingMode = "rate" | "yen";
export type LinePricingCtx = {
  /** 粗利率の既定（★B・単価が空の行でパートナーを直したとき） */
  marginPct: number;
  /** 上乗せ後の単価の丸め（1／10／100円・切り上げ） */
  round: number;
  /** 率で／円で（画面の中だけ。保存しない） */
  mode: PricingMode;
  setMode: (m: PricingMode) => void;
};

/**
 * 行の詳細の下段：［率で｜円で］ 粗利率［ ］％ ＋ パートナー［ ］％ ＝ 上乗せ率［ ］％ ？（docs/40 §5）。「円で」は 上乗せ額［ ］円 ＋ パートナー。
 * 仕入れの無い行（自社・未定）は粗利率・上乗せ率が「—」、パートナーは直せる（単価は動かさない）。
 * 欄の id（f_margin・f_ref・f_markup・f_markup_yen）は見積書の Enter・Tab の順路（#215、Estimate.tsx の PRICING_ORDER）が見る
 * 率の行・値引きはパートナーの欄だけ。パートナーの欄は見積にパートナーがあるときだけ（docPct が null なら出さない）
 */
export function LinePricing({ r, docPct, ctx, upd }: { r: LineInput; docPct: number | null; ctx: LinePricingCtx; upd: (p: Partial<LineInput>) => void }) {
  const [err, setErr] = useState<string | null>(null);
  const isDetail = r.role === "detail";
  const showRef = docPct != null && isReferralTarget(r);
  if (!isDetail && !showRef) return null;
  const cost = isDetail && !r.costOwn && r.costAmount != null && r.costAmount > 0 ? r.costAmount : null;
  const f = r.referralPct ?? docPct ?? 0;
  const sale = saleOf(r);
  const m = cost != null && sale != null ? pct2(marginOfPrice(cost, sale, f)) : null;
  const p = cost != null && sale != null ? pct2(markupOfPrice(cost, sale)) : null;
  const yenUp = cost != null && sale != null ? Math.round(sale - cost) : null;
  const apply = (edit: LinePricingEdit) => {
    const res = repriceLine({ costAmount: cost, quantity: r.quantity, unitPrice: r.unitPrice, referralPct: r.referralPct ?? null }, docPct, edit, ctx.round, ctx.marginPct);
    if (!res.ok) {
      setErr(res.error === "sumOver" ? REFERRAL_MSG.sumOver : null);
      return;
    }
    setErr(null);
    const patch: Partial<LineInput> = {};
    if (res.unitPrice != null && res.unitPrice !== r.unitPrice) patch.unitPrice = res.unitPrice;
    if (edit.kind === "referral" && (res.referralPct ?? null) !== (r.referralPct ?? null)) patch.referralPct = res.referralPct;
    if (Object.keys(patch).length) upd(patch);
  };
  /** パートナーの欄：見積の率と同じ・空は「見積の率に従う」（null） */
  const onRef = (v: number | null) => {
    if (v != null && (v < 0 || v >= 90)) {
      setErr(t(REFERRAL_MSG.pctRange));
      return;
    }
    apply({ kind: "referral", value: v == null || v === docPct ? null : v });
  };
  const refField = showRef ? (
    <label className="lp-f">
      {t(REFERRAL_MSG.referral)}
      <PctField id="f_ref" value={f} onCommit={onRef} aria-label={t(REFERRAL_MSG.referral)} data-testid="line-referral" className={r.referralPct != null ? "is-custom" : ""} />%
    </label>
  ) : null;
  const dash = <span className="muted lp-dash">—</span>;
  const help = t((showRef ? REFERRAL_MSG.lineHelp : REFERRAL_MSG.lineHelpNoRef).replace("{round}", String(ctx.round)));
  return (
    <span className="lprice" data-testid="line-pricing">
      {isDetail ? <span className="muted lp-head">{REFERRAL_MSG.pricingHead}</span> : null}
      {isDetail ? (
        <span className="seg sm" role="group" aria-label={REFERRAL_MSG.pricingBy}>
          <button type="button" aria-pressed={ctx.mode === "rate"} onClick={() => ctx.setMode("rate")} data-testid="pricing-rate">{REFERRAL_MSG.byRate}</button>
          <button type="button" aria-pressed={ctx.mode === "yen"} onClick={() => ctx.setMode("yen")} data-testid="pricing-yen">{REFERRAL_MSG.byYen}</button>
        </span>
      ) : null}
      {isDetail && ctx.mode === "rate" ? (
        <>
          <label className="lp-f">
            {cost != null ? <PctField id="f_margin" value={m} onCommit={(v) => v != null && apply({ kind: "margin", value: v })} aria-label={REFERRAL_MSG.margin} data-testid="line-margin" /> : dash}%
          </label>
          {refField ? <><span className="lp-op">{REFERRAL_MSG.plus}</span>{refField}</> : null}
          <span className="lp-op">{REFERRAL_MSG.eq}</span>
          <label className="lp-f">
            {REFERRAL_MSG.markup}
            {cost != null ? <PctField id="f_markup" value={p} onCommit={(v) => v != null && v >= 0 && apply({ kind: "markupPct", value: v })} aria-label={REFERRAL_MSG.markup} data-testid="line-markup" /> : dash}%
          </label>
        </>
      ) : isDetail ? (
        <>
          <label className="lp-f">
            {cost != null ? <NumInput kind="int" id="f_markup_yen" value={yenUp} onChange={(v) => v != null && apply({ kind: "markupYen", value: v })} aria-label={`${REFERRAL_MSG.markupYen}（円）`} data-testid="line-markup-yen" /> : dash}円
          </label>
          {refField ? <><span className="lp-op">{REFERRAL_MSG.plus}</span>{refField}</> : null}
        </>
      ) : (
        refField
      )}
      <Help text={help} label={REFERRAL_MSG.helpLabel} testId="help-line-pricing" />
      {err ? <span className="lp-err" role="alert" data-testid="line-pricing-error">{err}</span> : null}
    </span>
  );
}
